import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface UrgentForecast {
  id: number;
  partner_id: number;
  partner_name: string;
  predicted_bags: number;
  order_by_date: string;
  urgency: string;
  urgency_display: string;
  member_count: number;
  top_products: Array<{
    product_name: string;
    predicted_bags: number;
    form_type: string;
  }>;
}

interface TrendingProduct {
  product_id: number;
  product_name: string;
  total_demand_bags: number;
  trend_direction: string;
  partner_count: number;
  member_count: number;
  mash_percentage: number;
  pellet_percentage: number;
}

interface LauraInsights {
  summary: {
    total_members: number;
    members_with_patterns: number;
    partners_with_forecasts: number;
    urgent_orders: number;
    total_predicted_bags: number;
  };
  urgent_forecasts: UrgentForecast[];
  trending_products: TrendingProduct[];
}

const LauraInsightsCard: React.FC = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState<LauraInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningAnalysis, setRunningAnalysis] = useState(false);

  useEffect(() => {
    loadInsights();
  }, []);

  const loadInsights = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/bridge-club/laura-insights/');
      setInsights(response.data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load Laura insights:', err);
      setError('Failed to load insights');
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    try {
      setRunningAnalysis(true);
      await api.post('/admin/bridge-club/run-laura-learning/');
      await loadInsights();
    } catch (err: any) {
      console.error('Failed to run analysis:', err);
    } finally {
      setRunningAnalysis(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays <= 7) return `In ${diffDays} days`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'order_now': return '#dc2626';
      case 'order_this_week': return '#f59e0b';
      case 'upcoming': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return '↑';
      case 'down': return '↓';
      default: return '→';
    }
  };

  if (loading) {
    return (
      <div className="card laura-insights-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🤖</span>
          <h3 style={{ margin: 0 }}>Laura Learning Insights</h3>
        </div>
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
          Loading insights...
        </div>
      </div>
    );
  }

  if (error || !insights) {
    return (
      <div className="card laura-insights-card" style={{ padding: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🤖</span>
          <h3 style={{ margin: 0 }}>Laura Learning Insights</h3>
        </div>
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '2rem' }}>
          <p>No forecast data available yet.</p>
          <button
            onClick={runAnalysis}
            disabled={runningAnalysis}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#059669',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              cursor: runningAnalysis ? 'not-allowed' : 'pointer',
              opacity: runningAnalysis ? 0.7 : 1,
            }}
          >
            {runningAnalysis ? 'Running Analysis...' : 'Run Laura Learning'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card laura-insights-card" style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '0.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span style={{ fontSize: '1.25rem' }}>🤖</span>
          <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Laura Learning Insights</h3>
        </div>
        <button
          onClick={runAnalysis}
          disabled={runningAnalysis}
          style={{
            padding: '0.375rem 0.75rem',
            backgroundColor: '#f3f4f6',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            cursor: runningAnalysis ? 'not-allowed' : 'pointer',
            opacity: runningAnalysis ? 0.7 : 1,
          }}
        >
          {runningAnalysis ? 'Running...' : 'Refresh'}
        </button>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem',
      }}>
        <div style={{
          textAlign: 'center',
          padding: '0.75rem',
          backgroundColor: '#f0fdf4',
          borderRadius: '0.5rem',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#059669' }}>
            {insights.summary.members_with_patterns}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Members Tracked</div>
        </div>
        <div style={{
          textAlign: 'center',
          padding: '0.75rem',
          backgroundColor: insights.summary.urgent_orders > 0 ? '#fef2f2' : '#f9fafb',
          borderRadius: '0.5rem',
        }}>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: insights.summary.urgent_orders > 0 ? '#dc2626' : '#6b7280'
          }}>
            {insights.summary.urgent_orders}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Urgent Orders</div>
        </div>
        <div style={{
          textAlign: 'center',
          padding: '0.75rem',
          backgroundColor: '#eff6ff',
          borderRadius: '0.5rem',
        }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#2563eb' }}>
            {insights.summary.total_predicted_bags}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Bags Forecasted</div>
        </div>
      </div>

      {/* Urgent Forecasts */}
      {insights.urgent_forecasts.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <h4 style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem' }}>
            Partners Needing to Order
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {insights.urgent_forecasts.slice(0, 5).map((forecast) => (
              <div
                key={forecast.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '0.5rem',
                  borderLeft: `4px solid ${getUrgencyColor(forecast.urgency)}`,
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                }}
              >
                <div style={{ minWidth: '150px' }}>
                  <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                    {forecast.partner_name}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    {forecast.predicted_bags} bags needed
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    color: getUrgencyColor(forecast.urgency)
                  }}>
                    {forecast.urgency_display}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Order by {formatDate(forecast.order_by_date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {insights.urgent_forecasts.length > 5 && (
            <button
              onClick={() => navigate('/admin/bridge-club/forecasts')}
              style={{
                width: '100%',
                marginTop: '0.5rem',
                padding: '0.5rem',
                backgroundColor: 'transparent',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                color: '#059669',
                fontSize: '0.875rem',
                cursor: 'pointer',
              }}
            >
              View all {insights.urgent_forecasts.length} forecasts
            </button>
          )}
        </div>
      )}

      {/* Trending Products */}
      {insights.trending_products.length > 0 && (
        <div>
          <h4 style={{ fontSize: '0.875rem', color: '#374151', marginBottom: '0.75rem' }}>
            Trending Products This Week
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {insights.trending_products.slice(0, 5).map((product, index) => (
              <div
                key={product.product_id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: index === 0 ? '#fef3c7' : '#f9fafb',
                  borderRadius: '0.375rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '1.25rem',
                    height: '1.25rem',
                    backgroundColor: index === 0 ? '#fbbf24' : '#e5e7eb',
                    color: index === 0 ? '#92400e' : '#6b7280',
                    borderRadius: '50%',
                    fontSize: '0.625rem',
                    fontWeight: 'bold',
                  }}>
                    {index + 1}
                  </span>
                  <span style={{ fontSize: '0.875rem' }}>{product.product_name}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontSize: '0.875rem', color: '#374151' }}>
                    {product.total_demand_bags} bags
                  </span>
                  <span style={{
                    color: product.trend_direction === 'up' ? '#059669' :
                           product.trend_direction === 'down' ? '#dc2626' : '#6b7280',
                    fontWeight: 'bold',
                  }}>
                    {getTrendIcon(product.trend_direction)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {insights.urgent_forecasts.length === 0 && insights.trending_products.length === 0 && (
        <div style={{ textAlign: 'center', color: '#6b7280', padding: '1rem' }}>
          <p>No forecast data available yet.</p>
          <p style={{ fontSize: '0.875rem' }}>
            Forecasts will appear once Bridge Club members have more purchase history.
          </p>
        </div>
      )}
    </div>
  );
};

export default LauraInsightsCard;
